import React, { useRef, useState } from "react";
import Navbar from "../components/Navbar";
import heroVideo from "../assets/hero.mp4";
import Footer from "./Footer";

const OurWork = () => {
  const videoRef = useRef(null);
  const sectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState("Advertising");

  const handleMouseEnter = () => videoRef.current?.play();
  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const services = [
    "Advertising",
    "Partner Marketing",
    "BYOB - Build Your Own Brand",
    "Video Production & Editing Services",
    "Collabs with Other Influencers",
  ];

  const handleScrollToSection = (section) => {
    setActiveSection(section);
    sectionRefs.current[section]?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-black text-white min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <div
        className="relative w-full h-[80vh] flex flex-col justify-center items-center text-center overflow-hidden"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Subtle background glow (theme match) */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-3xl" />
          <div className="absolute -bottom-40 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>

        <video
          ref={videoRef}
          className="absolute top-0 left-0 w-full h-full object-cover"
          src={heroVideo}
          muted
          loop
        />
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-black/70 via-black/55 to-black/80"></div>
        <div className="relative z-10 px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white/95">
            THE WORLD’S LEADING SOCIAL & INFLUENCER MARKETING AGENCY
          </h2>
          <h3 className="text-lg sm:text-2xl md:text-3xl font-bold text-white/70 mt-2 sm:mt-4">
            POWERED BY CREATORS
          </h3>
        </div>
      </div>

      {/* Services Navigation */}
      <div className="relative overflow-hidden border-b border-white/10 bg-black">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 left-1/3 h-[26rem] w-[26rem] -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="absolute -bottom-32 left-2/3 h-[26rem] w-[26rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative overflow-x-auto lg:overflow-hidden whitespace-nowrap py-4 flex items-center justify-start px-4 md:px-8 scrollbar-hide w-full">
          <div className="flex space-x-3 md:space-x-4 w-max">
          {services.map((item, index) => (
            <button
              key={index}
              className={`text-xs sm:text-sm uppercase tracking-wide px-4 md:px-5 py-2 rounded-full border transition-colors ${
                activeSection === item
                  ? "text-orange-500 border-orange-500/40 bg-orange-500/10 font-bold"
                  : "text-white/70 border-white/10 hover:text-orange-500 hover:border-orange-500/40 hover:bg-orange-500/5"
              }`}
              onClick={() => handleScrollToSection(item)}
            >
              {item}
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* Dynamic Content Sections */}
      <section className="relative overflow-hidden py-8 md:py-12 px-4 lg:px-8 bg-black">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-44 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="absolute -bottom-44 left-2/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto text-left">
        {services.map((service, index) => (
          <div
            key={index}
            ref={(el) => (sectionRefs.current[service] = el)}
            id={service}
            className="scroll-mt-24 py-2"
          >
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur">
              <h2 className="text-xl md:text-2xl font-bold mb-3">{service}</h2>
              <p className="text-white/70 mb-0 text-xs md:text-sm leading-relaxed">
              {service === "Advertising" && (
                <>
                  As an influencer, you can now earn more by sharing advertising reels with your followers on Instagram. With our service, you’ll receive both a <span className="text-orange-500 font-bold">Fixed Payout</span> and a <span className="text-orange-500 font-bold">Variable Payout</span> based on your engagement and reach metrics.
                  <br /><br />
                  <span className="text-orange-500 font-bold">Fixed Payout:</span> You’ll earn a guaranteed amount for each reel you share.
                  <br />
                  <span className="text-orange-500 font-bold">Variable Payout:</span> Your earnings grow with the performance of the reel, based on factors like engagement (likes, comments, shares) and reach (how many people see the content).
                  <br /><br />
                  It’s a simple, rewarding way to monetize your influence on Instagram while offering value to brands. Join us today and start earning with every reel you share!
                  <br /><br />
                  <span className="text-orange-500 font-bold">Here's how Infuber helps you:</span>
                  <br /><br />
                  <span className="text-orange-500 font-bold">Discover Brands & Campaigns:</span> We connect you with top brands that align with your style and audience, ensuring that you get relevant and exciting advertising opportunities.
                  <br />
                  <span className="text-orange-500 font-bold">Maximized Earnings:</span> For every reel you share, you'll earn a fixed payout, plus a variable payout that increases based on how well your content performs. This means more earnings when your engagement and reach are high.
                  <br />
                  <span className="text-orange-500 font-bold">Performance-Based Rewards:</span> Your earnings grow as your reels attract more interaction from your audience. Infuber ensures you are paid for the true value you bring to the campaign.
                  <br />
                  <span className="text-orange-500 font-bold">Seamless Campaign Management:</span> nfuber manages the logistics of the campaign, ensuring you get paid on time, and that all content guidelines and expectations are clear and easy to follow.
                </>
              )}
              {service === "Partner Marketing" && (
                <>
                  <span className="text-orange-500 font-bold">Earn with Every Sale through Infuber’s Affiliate Marketing Program!</span><br />
                  At Infuber, we offer you a seamless way to monetize your Instagram presence through affiliate marketing. When you share a brand’s product with your followers, you can earn a commission on every sale made through your unique affiliate link.
                  <br />
                  <span className="text-orange-500 font-bold">How Infuber Helps You:</span><br />
                  <span className="text-orange-500 font-bold">Find the Best Brands:</span> We connect you with reputable brands that align with your audience and niche, so you can promote products you genuinely believe in.<br />
                  <span className="text-orange-500 font-bold">Agreed-Upon Commission:</span> For every sale made through your affiliate link, you’ll earn a percentage of the sale price, agreed upon with the brand beforehand. Your earnings grow with each successful transaction.<br />
                  <span className="text-orange-500 font-bold">Track Performance Easily:</span> Infuber provides easy-to-use tools to track the clicks and conversions from your affiliate link, so you can see exactly how much you’re earning.<br />
                  <span className="text-orange-500 font-bold">Hassle-Free Campaign Management:</span>We handle all the back-end logistics, ensuring smooth collaboration with brands, clear payout terms, and timely payments.<br />
                  <br />
                  With Infuber’s affiliate marketing program, you can turn your authentic product recommendations into a lucrative revenue stream while focusing on what you do best – creating content and engaging with your followers.                </>
              )}
              {service === "BYOB - Build Your Own Brand" && (
                <>
                  <span className="text-orange-500 font-bold">Launch your own brand and grow with Infuber’s strategic guidance.</span><br />
                  At Infuber, we empower influencers like you to create and sell products under your very own personal brand. Whether it's merchandise, accessories, or unique products, we provide everything you need to bring your vision to life.<br />
                  <br />
                  <span className="text-orange-500 font-bold">How Infuber Supports Your Brand:</span><br />
                  <span className="text-orange-500 font-bold">FSet Your Own Price:</span> You have full control over setting the price for your products, allowing you to maximize your profits while offering great value to your customers.<br />
                  <span className="text-orange-500 font-bold">Revenue Sharing:</span> Infuber retains a small percentage of each sale to cover operational costs and share with the manufacturer. The rest of the profits go directly to you.<br />
                  <span className="text-orange-500 font-bold">Seamless Fulfillment:</span> We manage the back-end logistics, including product creation, inventory, and shipping, so you can focus on marketing and growing your brand.<br />
                  <span className="text-orange-500 font-bold">Promote Your Brand:</span> We provide the tools and support to help you effectively market your products and connect with your audience.<br />
                  <br />
                  With Infuber, building and selling your own branded products has never been easier. You focus on creating a brand that speaks to your followers, and we handle the rest, giving you the freedom to earn on your terms.
                </>)}
              {service === "Video Production & Editing Services" && (
                <>
                  <span className="text-orange-500 font-bold">Elevate your content with professional video production & editing.</span><br />
                  At Infuber, we understand the power of video content in today’s digital world. That’s why we offer professional video production and editing services to help you create high-quality, engaging videos that captivate your audience and enhance your brand presence.<br />
                  <span className="text-orange-500 font-bold">How Infuber Enhances Your Video Content:</span><br />
                  <span className="text-orange-500 font-bold">Creative Video Production:</span> From concept to final cut, we assist in creating visually stunning videos that align with your brand’s voice and messaging. Whether it's promotional content, tutorials, or behind-the-scenes footage, we've got you covered.<br />
                  <span className="text-orange-500 font-bold">Expert Editing Services:</span> Our expert team handles everything from color correction to sound design and transitions, ensuring your videos look polished and professional.<br />
                  <span className="text-orange-500 font-bold">Tailored for Social Media:</span> We optimize your videos for the best performance on platforms like Instagram, YouTube, TikTok, and more. Whether you need Stories, Reels, or long-form YouTube videos, we tailor the format to your audience.<br />
                  <span className="text-orange-500 font-bold">Fast Turnaround Times:</span> With Infuber, you get high-quality videos without the wait. Our efficient team ensures quick editing and production times, so you stay on top of trends.<br />
                  <span className="text-orange-500 font-bold">Increase Engagement:</span> Well-crafted videos are proven to drive higher engagement rates. With Infuber’s services, your videos will not only look great but also resonate with your followers, boosting interaction and brand loyalty.<br />

                  <br />
                  Whether you need help with scriptwriting, filming, or post-production editing, Infuber is here to take your video content to the next level, helping you create videos that truly stand out.                </>
              )}
              {service === "Collabs with Other Influencers" && (
                <>
                  <span className="text-orange-500 font-bold">Expand your reach by collaborating with other influencers through Infuber.</span><br />
                  At Infuber, we understand the power of video content in today’s digital world. That’s why we offer professional video production and editing services to help you create high-quality, engaging videos that captivate your audience and enhance your brand presence.<br />
                  <span className="text-orange-500 font-bold">How Infuber Makes Collaborations Easy:</span><br />
                  <span className="text-orange-500 font-bold">Find the Right Influencers:</span> We help you identify influencers who align with your values, niche, and target audience, ensuring that your collaborations feel authentic and mutually beneficial.<br />
                  <span className="text-orange-500 font-bold">Joint Content Creation:</span>Whether it’s co-hosting live streams, creating collaborative posts, or planning joint campaigns, Infuber facilitates the creative process to ensure that your content stands out and resonates with both audiences.<br />
                  <span className="text-orange-500 font-bold">Maximize Audience Reach: </span> Collaborating with other influencers helps expand your reach to new followers, allowing both of you to grow your audiences while creating exciting, cross-promotional content.<br />
                  <span className="text-orange-500 font-bold">Seamless Collaboration Management: </span> Infuber handles the logistics of collaboration, including timelines, content approvals, and communication, so you can focus on what matters most: creating engaging content.<br />
                  <span className="text-orange-500 font-bold">Boost Engagement & Growth:</span> Partnering with influencers in complementary niches can significantly boost your engagement, broaden your brand’s reach, and lead to new opportunities and partnerships.<br />
                  <br />
                  With Infuber, influencer collaborations are made simple, strategic, and impactful. Team up with other influencers and watch your community grow!
                </>)}
              </p>
            </div>
          </div>
        ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OurWork;
